import type {
  GetUserOpportunitiesResponseDto,
  SolutionTypeResponse,
  UserResponse,
} from '~/generated/api'

export interface DropdownItems {
  [key: string]: any
}

export interface DeleteProjectMembersRequest {
  projectId: string
  memberId: string
}

export interface AggregatedRiskDefinition {
  id: string
  criteria: string[]
  level: number
  type: 'impact' | 'probability'
}

export interface LanguageItem {
  name: string
  code: string
  tag: string
}

export interface PreferredLanguage {
  id: string
  name: string
  code: string
  isDefault: boolean
  tag: string
}

export interface Translation {
  translatedText: string
}

export interface TranslationSource {
  originalText: string
  translations: Translation[]
}

export interface User {
  businessPhones?: Array<string>
  displayName?: string
  givenName?: string
  id?: string
  jobTitle?: string
  mail?: string
  mobilePhone?: string
  officeLocation?: string
  preferredLanguage?: string
  surname?: string
  userPrincipalName?: string
}

export type OpportunityOption = GetUserOpportunitiesResponseDto & {
  combinedName: string
  occupied?: boolean
  siteScopeText?: string
  errorMessage?: string
  errorMessageParams?: Record<string, string>
}

export interface ProjectPatchRequest {
  name: string
  status: string
  opportunityId?: string | null
  accountSiteId?: string
  uploadedToMySecuritas?: 'NOT_UPLOADED' | 'UPLOADED' | 'PROCESSED'
}

export interface ProjectMember {
  id: string
  ownerEmail: string
  ownerEntraIdOid: string
  memberEmail: string
  memberEntraIdOid: string
}

/**
 * Outcome of an invite. `reconciled` means the server acknowledged success with
 * a 2xx but returned no member, and re-reading the list supplied it. It is
 * never inferred from an error status or from a missing response.
 */
export type AddProjectMemberResult =
  | { status: 'created', member: ProjectMember }
  | { status: 'reconciled', member: ProjectMember }
  | { status: 'failed', statusCode?: number, error?: unknown }

export interface RemovedRisk {
  areaId: string
  riskId: string
  riskTypeId: string
  name: string
}

export interface RsvDropdownItem {
  id: string
  name: string
}

export interface SubItem {
  translate_key: string
  link: string
}

export interface MenuItem {
  icon: string
  link: string
  translate_key: string
  subItems?: SubItem[]
}

export interface LevelMapping {
  name: string
  color: string
  highlightedColor: string
}

export interface Severity {
  risk: string
  color: string
}

export interface RiskLevel extends LevelMapping {
  impactTooltip: boolean
  probabilityTooltip: boolean
  impactDescriptions: string[]
  probabilityDescriptions: string[]
}

export interface ReportPageInfo {
  title: string
  pageAmount: number
  pageNumber: number
}

export interface SortByOptions {
  name: string
}

export interface UploadToMySecuritasRequest {
  projectId: string
  protectasUser: string
  clientId: string
  clientName: string
  siteId?: string
  category: 'Site Assessment Report' | 'Site Assessment Executive Summary'
  languageId: string
  languageTag: string
}

export interface CreateWorkspaceRequest {
  name: string
  administratorId: string
  riskValueChainIds: string[]
  userIds: string[]
}

export interface SolutionImage {
  id: string
  filename: string
  mimetype: string
  downloadUrl: string
}

export interface ScenarioImage {
  id: string
}

export interface RiskType {
  id: string
  name: string
}

export interface ScenarioType {
  id: string
  name: string
  createdAt?: string
  isCustom?: boolean
}

export interface SuggestedRisks {
  recommendedRisks: RiskType[]
  allRisks: RiskType[]
}

export interface SolutionTypeCategory {
  id: string
  name: string
  subCategories?: SolutionTypeSubCategory[]
}

export interface SolutionTypeSubCategory {
  id: string
  name: string
}

export interface AreaType {
  id?: string
  name: string
  isCustom?: boolean
  createdAt: string
}

export interface Risk {
  id?: string
  name?: string
  scenarios?: Scenario[]
  suggestedScenarios?: ScenarioType[]
  impact?: number | null
  probability?: number | null
  description?: string
  strategy?: string
  strategyReason?: string
  riskType: RiskType
}

export interface RiskTypeRecommendation {
  id: string
  riskType: RiskType
  scenarioTypeRecommendations: ScenarioTypeRecommendation[]
}

export interface ScenarioTypeRecommendation {
  id: string
  scenarioType: ScenarioType
  solutionTypeRecommendations: SolutionTypeRecommendation[]
}

export interface SolutionTypeRecommendation {
  id: string
  solutionType: SolutionType
}

export interface Scenario {
  scenarioType: ScenarioType
  scenarioTypeId: string
  id: string
  when?: string[]
  description?: string
  images?: ScenarioImage[]
  solutions?: Solution[]
}

export interface Solution {
  id: string
  image?: string
  solutionType: SolutionType
}

export interface SolutionType {
  id: string
  name: string
  updatedAt: string
  isCustom?: boolean
  images?: SolutionImage[]
  description?: string
  category?: SolutionTypeCategory
  subCategory?: SolutionTypeSubCategory
}

export function normalizeSolutionType(
  solutionType: SolutionTypeResponse,
): SolutionType {
  return {
    ...solutionType,
    description: solutionType.description ?? undefined,
    images: solutionType.images?.map(image => ({
      id: image.id,
      filename: image.filename,
      mimetype: image.mimetype,
      downloadUrl: image.downloadUrl ?? image.blobUrl,
    })),
  }
}

export interface PdfRisk extends Risk {
  areaName: string
  scenarioPagination: Scenario[][]
  pagination: any[]
}

export interface RiskDefinition {
  id: string
  criteria: string
  level: number
  type: 'impact' | 'probability'
}

export interface DefinitionSet {
  id: string
  uuid?: string
  version: number
  name: string
  definitions: RiskDefinition[]
}

export interface Area {
  id?: string
  // Null for an area that takes its name from its area type.
  name?: string | null
  areaType?: AreaType | null
  risks?: Risk[]
  risksSeverity?: string
  mainRisks?: Risk[]
  suggestedRisks?: SuggestedRisks
}

export interface RiskValueChain {
  id: string
  name: string
  definitionSet: DefinitionSet
  availableAreaTypes: Area[]
  createdAt: string
  workspaces: { workspace: Workspace, id: string }[]
  workspacePools: { workspace: Workspace, id: string }[]
}

export interface Project {
  id: string
  name: string
  image?: string
  riskValueChain: RiskValueChain
  opportunity: GetUserOpportunitiesResponseDto | null
  opportunityId: string | null
  agentInitials?: string
  status?: string
  // The API sends null for a project without a selected site.
  accountSiteId?: string | null
  areas: Area[]
  // The API sends null when the creator's account no longer exists.
  createdBy?: {
    id: string
    entraIdOid: string
  } | null
  createdAt: string
  updatedAt: string
  uploadedToMySecuritas?: 'NOT_UPLOADED' | 'UPLOADED' | 'PROCESSED'
  isConfidential?: boolean
}

export interface ManagedUser {
  id: string
  name: string
  email: string
  createdProjectsCount: number
  completedProjectsCount: number
  entraIdOid: string
  language: {
    code: string
    name: string
    iso: string
    file: string
  }
  roles: UserRole[]
}

/**
 * The current user as returned by `GET /v1/users/me` and by the language
 * update. Workspace fields are id strings, not `Workspace` objects.
 */
export type UserContext = UserResponse

export interface UserRole {
  id: string
  name: string
}

export interface Workspace {
  id: string
  createdAt: string
  updatedAt: string
  name: string
  members: ManagedUser[]
  administrators: ManagedUser[]
  riskValueChains: RiskValueChain[]
  riskValueChainPool: RiskValueChain[]
}
